import React from "react";
import { Clock } from "../../../components/icons/svg/outline";
import {
  formatDashboardCount,
  formatDashboardDelta,
  formatDashboardHours,
  isNoHoursPayoutDashboardRow,
} from "./summaryUtils";

/**
 * Renders one compact payout timesheet dashboard table row.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.timesheet - Timesheet row data.
 * @param {Function} props.onSelect - Row click handler.
 * @param {boolean} props.quickBooksExported - Whether QuickBooks was exported for the week.
 * @returns {JSX.Element} Compact table row.
 */
const PayoutTimesheetCompactRow = ({ timesheet, onSelect, quickBooksExported = false }) => {
  const hasWorkerHours = timesheet.workerHours !== null && timesheet.workerHours !== undefined;
  const hasManagerHours = Number(timesheet.hoursLogHours) > 0;
  const hasPayoutHours = timesheet.payoutHours !== null &&
    timesheet.payoutHours !== undefined &&
    Number(timesheet.payoutHours) !== 0;
  const hasManagerDelta = hasWorkerHours && hasManagerHours;
  const hasPayoutDelta = hasWorkerHours && hasPayoutHours;
  const hasCompleteHoursLogs = timesheet.workerShiftsHaveHoursLogs;
  const isPrePayout = ["pending", "approving"].includes(timesheet.status);
  const isActionable = timesheet.isActionable !== false;
  const isNoHours = isNoHoursPayoutDashboardRow(timesheet, quickBooksExported);
  const showHoursLogWarning = hasManagerHours && !hasCompleteHoursLogs;
  const showHoursLogComplete = hasManagerHours && hasCompleteHoursLogs;
  const showDeltaHoursLogWarning = isPrePayout && showHoursLogWarning;
  const showDeltaHoursLogComplete = isPrePayout && showHoursLogComplete;
  const hoursLogWarning = "Some manager hours logs are missing or incomplete";
  const hoursLogComplete = "All shifts have manager hours logs completed";
  const managerHoursMissing = "Manager hours logs missing";
  const managerDelta = Number(timesheet.hoursLogHours || 0) - Number(timesheet.workerHours || 0);
  const payoutDelta = Number(timesheet.payoutHours || 0) - Number(timesheet.workerHours || 0);
  const formatDeltaDisplay = (value) => Number(value) === 0 ? "0" : formatDashboardDelta(value);
  const managerDeltaTitle = showDeltaHoursLogWarning
    ? hoursLogWarning
    : showDeltaHoursLogComplete
      ? hoursLogComplete
      : hasManagerHours
        ? "Difference between worker hours and manager hours."
        : managerHoursMissing;
  const payoutDeltaTitle = hasPayoutHours
    ? "Difference between worker hours and payout hours."
    : "Payout hours missing";
  const hoursLogValueClass = showHoursLogWarning
    ? "text-yellow-800"
    : showHoursLogComplete
      ? "text-green-900"
      : "";
  const deltaValueClass = showDeltaHoursLogWarning
    ? "text-yellow-800"
    : showDeltaHoursLogComplete
      ? "text-green-900"
      : "";
  const statusConfig = {
    pending: {
      label: "Pending",
      className: "border-gray-200 bg-gray-100 text-gray-700",
    },
    approving: {
      label: "Approving",
      className: "border-yellow-200 bg-yellow-50 text-yellow-800",
    },
    approved: {
      label: "Approved",
      className: "border-green-200 bg-green-50 text-green-800",
    },
    exported: {
      label: "Exported",
      className: "border-green-900 bg-green-900 text-white",
    },
    edited: {
      label: "Edited",
      className: "border-orange-200 bg-yellow-200 text-orange-900",
    },
  };
  const status = isNoHours
    ? {
      label: "No Hrs",
      className: "border-gray-200 bg-gray-100 text-gray-700",
    }
    : statusConfig[timesheet.status] || statusConfig.approving;
  const statusTitle = isNoHours
    ? "QuickBooks was exported and this worker has no shifts for the week."
    : undefined;
  const rowClassName = isActionable
    ? "group cursor-pointer text-xs text-gray-700 transition hover:bg-primary-50/60"
    : "group cursor-default bg-gray-50/40 text-xs text-gray-600";

  return (
    <tr
      className={rowClassName}
      onClick={() => {
        if (isActionable) {
          onSelect(timesheet.worker, timesheet.weekEnding);
        }
      }}
      aria-disabled={!isActionable}
    >
      <td className="truncate whitespace-nowrap px-3 py-1 text-gray-950">
        <span title={timesheet.workerName}>{timesheet.workerName}</span>
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatDashboardCount(timesheet.shiftCount)}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        {formatDashboardHours(timesheet.workerHours)}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        <span
          className={`inline-flex items-center justify-end gap-1 ${hoursLogValueClass}`}
          title={showHoursLogWarning ? hoursLogWarning : showHoursLogComplete ? hoursLogComplete : managerHoursMissing}
        >
          {showHoursLogWarning ? <Clock className="h-3 w-3 flex-shrink-0" /> : null}
          {hasManagerHours ? formatDashboardHours(timesheet.hoursLogHours) : "-"}
        </span>
      </td>
      <td className="px-2 py-1 text-right tabular-nums text-gray-950">
        {formatDashboardHours(timesheet.payoutHours)}
      </td>
      <td className="px-2 py-1 text-right tabular-nums">
        <span
          className={`inline-flex items-center justify-end gap-1 ${deltaValueClass}`}
          title={managerDeltaTitle}
        >
          {showDeltaHoursLogWarning ? <Clock className="h-3 w-3 flex-shrink-0" /> : null}
          {hasManagerDelta ? formatDeltaDisplay(managerDelta) : "-"}
        </span>
      </td>
      <td className="px-2 py-1 text-right tabular-nums" title={payoutDeltaTitle}>
        {hasPayoutDelta ? formatDeltaDisplay(payoutDelta) : "-"}
      </td>
      <td className="py-1 pl-12 pr-6 text-left">
        <span
          className={`inline-flex min-w-16 text-center justify-center rounded-full border px-2 py-0.5 text-[11px] ${status.className}`}
          title={statusTitle}
        >
          {status.label}
        </span>
      </td>
    </tr>
  );
};

export default PayoutTimesheetCompactRow;
